// One draw call per tile for the fragments that only appear once or
// twice in it.
//
// Measured on the live SpaceX export (113,909 hashes, 1,441,461 rows):
// 51.1% of hashes have a SINGLE instance and together carry 4.0% of the
// geometry; 75.5% have two or fewer and carry 7.9%. Each of those is an
// InstancedMesh of its own in the pool path, so half the draw calls
// render a twentieth of the model. A headed capture on prod read 9,290
// draw calls against 12,899 pools with only 432 tiles visible, and the
// triangle count (4.1M) was well inside its budget: draw calls are the
// ceiling, not triangles.
//
// A BatchedMesh is the answer to exactly that shape. It holds many
// geometries in one buffer pair with a per-item matrix and draws them
// with one multi-draw call, so a tile's whole long tail becomes one
// call. The cost is that a geometry is copied per item rather than
// instanced, which is why only the hashes with very few instances go
// here and everything denser stays in the pools.
//
// What makes it applicable at all is the exporter's material contract:
// every fragment GLB carries ONE generic material and puts colour in
// COLOR_0, so all opaque fragments share a material and the only split
// is opaque versus blended.
//
// The mesh this builds is deliberately shaped to pass for an
// InstancedMesh where the rest of the pipeline touches it:
//
//   userData[id]              the sidecar row, as the pool writes it
//   userData.snapshotMatrices what the matrix would be if visible
//   userData.baseColors       what the colour would be without highlight
//   setMatrixAt/getMatrixAt   already native to BatchedMesh
//   setColorAt                added here, via batchColor.js
//   instanceMatrix/instanceColor
//                             inert flag holders, so the adapter and
//                             the applicator can set needsUpdate on
//                             them without a branch
//
// so MapMeshesAdapter, InstanceStateApplicator and the picker work on a
// batched item the same way they work on a pooled one.
import { BatchedMesh, BufferAttribute, Frustum, Matrix4, Sphere } from 'three';
import { attachBatchTint, disposeBatchTint, setBatchGhostAt, setBatchTintAt } from './batchColor';
import { applySurfaceShading } from './surfaceShading';
import { singlePassBlend } from './blendPass';

const ZERO_MATRIX = new Matrix4().makeScale(0.0001, 0.0001, 0.0001);

// Is this the "not drawn" matrix every writer uses (here, the applicator
// and the adapter all write makeScale(0.0001))? Read back through the
// batch's float texture it is 0.0001 to float32 precision, hence the
// tolerance rather than ===.
export function isParkedMatrix(matrix) {
  const e = matrix && matrix.elements;
  if (!e) return false;
  const near = (v) => Math.abs(v - 0.0001) < 1e-8;
  return near(e[0]) && near(e[5]) && near(e[10])
    && e[1] === 0 && e[2] === 0 && e[4] === 0 && e[6] === 0 && e[8] === 0 && e[9] === 0;
}

// Every geometry in one BatchedMesh must carry the same attributes with
// the same item sizes, and either all or none may be indexed. Fragment
// GLBs vary: some carry uv, some have three-component colour, some
// four. So each geometry is reduced once to position/normal/colour with
// colour always four-wide, and the result is cached on the source.
export function normalizeForBatch(geometry) {
  if (!geometry) return null;
  if (geometry.userData && geometry.userData.__batchGeometry) return geometry.userData.__batchGeometry;

  const position = geometry.getAttribute('position');
  if (!position) return null;

  const out = geometry.clone();
  // Drop everything the batch does not consume, so two fragments never
  // disagree about their attribute set.
  for (const name of Object.keys(out.attributes)) {
    if (name !== 'position' && name !== 'normal' && name !== 'color') out.deleteAttribute(name);
  }
  if (!out.getAttribute('normal')) out.computeVertexNormals();

  const colour = out.getAttribute('color');
  if (!colour) {
    const white = new Float32Array(position.count * 4);
    white.fill(1);
    out.setAttribute('color', new BufferAttribute(white, 4));
  } else if (colour.itemSize === 3) {
    const rgba = new Float32Array(colour.count * 4);
    for (let i = 0; i < colour.count; i++) {
      rgba[i * 4] = colour.getX(i);
      rgba[i * 4 + 1] = colour.getY(i);
      rgba[i * 4 + 2] = colour.getZ(i);
      rgba[i * 4 + 3] = 1;
    }
    out.setAttribute('color', new BufferAttribute(rgba, 4));
  }

  // All or nothing on the index: give the unindexed ones a trivial one
  // rather than splitting the batch in two.
  if (!out.index) {
    const count = out.getAttribute('position').count;
    const index = count > 65535 ? new Uint32Array(count) : new Uint16Array(count);
    for (let i = 0; i < count; i++) index[i] = i;
    out.setIndex(new BufferAttribute(index, 1));
  }

  out.userData = {};
  ensureBounds(out);
  if (!geometry.userData) geometry.userData = {};
  geometry.userData.__batchGeometry = out;
  return out;
}

function sizeOf(geometry) {
  const position = geometry.getAttribute('position');
  return {
    vertices: position ? position.count : 0,
    indices: geometry.index ? geometry.index.count : 0,
  };
}

// ---------------------------------------------------------------------
// WHAT A FRAME COSTS ON THE CPU, PER BATCH.
//
// three 0.164's BatchedMesh.onBeforeRender runs for every batch on every
// render call: for every item it reads the matrix out of the float
// texture, copies the item's bounding sphere, transforms it, tests it
// against the frustum (and with sortObjects, measures and sorts the
// list). Tom's round 9 orbit trace (real GPU, after settle): onBeforeRender
// 1.1 s self, getBoundingSphereAt 0.4-1.0 s, copy 0.4-0.6 s,
// multiplyMatrices 0.3 s - in the 50 ms frames the view draws ~3 times a
// second at rest, with the camera not moving at all.
//
// The answer for a camera that has not moved, and for items that have
// not changed, is the answer the last frame got. So:
//   - the multi-draw list three built is KEPT while the camera (projection,
//     view and the batch's own world matrix) and the items (matrices,
//     visibility, additions) are unchanged - one 48-number compare;
//   - a batch whose whole bounding sphere is inside the frustum skips the
//     per-item test (every visible item is drawn either way);
//   - the opaque batch is not sorted: the depth test decides what is seen,
//     and the sort cost n log n per moving frame per batch.
// Per-item culling itself stays: it is why a batch draws fewer triangles
// than the same items in pools.
// ---------------------------------------------------------------------
const _batchOnBeforeRender = BatchedMesh.prototype.onBeforeRender;
const _cullMatrix = new Matrix4();
const _cullFrustum = new Frustum();
const _itemMatrix = new Matrix4();
const _itemSphere = new Sphere();

// Does the camera (or the batch) differ from the last list's? Writes the
// new key as it compares, so it is one pass either way.
function cameraMoved(mesh, camera) {
  let key = mesh._cullKey;
  let moved = false;
  if (!key || mesh._cullCamera !== camera) {
    key = mesh._cullKey = new Float64Array(48);
    mesh._cullCamera = camera;
    moved = true;
  }
  const parts = [camera.projectionMatrix.elements, camera.matrixWorldInverse.elements, mesh.matrixWorld.elements];
  let k = 0;
  for (let p = 0; p < 3; p++) {
    const e = parts[p];
    for (let i = 0; i < 16; i++, k++) {
      if (key[k] !== e[i]) { key[k] = e[i]; moved = true; }
    }
  }
  return moved;
}

// The local-frame sphere around every visible item, recomputed only after
// an item changed (O(items) once, never per frame).
export function batchSphereOf(mesh) {
  if (!mesh._batchSphere) mesh._batchSphere = new Sphere();
  if (!mesh._batchSphereDirty && mesh._batchSphereReady) return mesh._batchSphere;
  const out = mesh._batchSphere.makeEmpty();
  const n = mesh._geometryCount || 0;
  for (let i = 0; i < n; i++) {
    if (!mesh._active[i] || !mesh._visibility[i]) continue;
    if (!mesh.getMatrixAt(i, _itemMatrix)) continue;
    const s = mesh.getBoundingSphereAt(i, _itemSphere);
    if (!s) continue;
    s.applyMatrix4(_itemMatrix);
    out.union(s);
  }
  mesh._batchSphereDirty = false;
  mesh._batchSphereReady = true;
  return out;
}

function sphereInside(frustum, sphere) {
  if (!sphere || sphere.isEmpty()) return false;
  const planes = frustum.planes;
  for (let i = 0; i < 6; i++) {
    if (planes[i].distanceToPoint(sphere.center) < sphere.radius) return false;
  }
  return true;
}

function cachedOnBeforeRender(renderer, scene, camera, geometry, material, group) {
  const stats = this._cullStats;
  const changed = this._cullDirty || this._visibilityChanged;
  const moved = cameraMoved(this, camera);
  if (!moved && !changed && this._cullReady) { stats.kept++; return; }
  if (changed) this._batchSphereDirty = true;
  let inside = false;
  if (this.perObjectFrustumCulled) {
    _cullMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse).multiply(this.matrixWorld);
    _cullFrustum.setFromProjectionMatrix(_cullMatrix, renderer && renderer.coordinateSystem);
    inside = sphereInside(_cullFrustum, batchSphereOf(this));
  }
  // three skips its rebuild when nothing it watches changed and neither
  // culling nor sorting is on - which is exactly the "inside" call below.
  this._visibilityChanged = true;
  if (inside) {
    stats.whole++;
    this.perObjectFrustumCulled = false;
    try { _batchOnBeforeRender.call(this, renderer, scene, camera, geometry, material, group); } finally { this.perObjectFrustumCulled = true; }
  } else {
    stats.culled++;
    _batchOnBeforeRender.call(this, renderer, scene, camera, geometry, material, group);
  }
  this._cullDirty = false;
  this._cullReady = true;
}

// Matrix writes that change nothing (the applicator re-asserts state in
// sweeps) must not throw the kept list away.
function sameMatrixAt(mesh, id, matrix) {
  const data = mesh._matricesTexture && mesh._matricesTexture.image && mesh._matricesTexture.image.data;
  if (!data || id >= mesh._geometryCount) return false;
  const e = matrix.elements;
  const o = id * 16;
  for (let i = 0; i < 16; i++) if (data[o + i] !== Math.fround(e[i])) return false;
  return true;
}

// Bounds of a geometry as a BatchedMesh item will need them. A batch
// copies geometry.boundingBox/boundingSphere into the item when they are
// there; when they are not, three scans every vertex of the item the
// first time the item is culled - and a rebuilt batch is a new mesh, so
// each rebuild paid every scan again. normalizeForBatch caches the
// geometry, so this runs once per fragment level, not per item.
function ensureBounds(geometry) {
  if (!geometry.boundingBox) geometry.computeBoundingBox();
  if (!geometry.boundingSphere) geometry.computeBoundingSphere();
  return geometry;
}

// What a batch is sized for. A first build is exact (most tiles never
// grow); a batch rebuilt because it ran out of room gets `headroom` more,
// so the next revive of that tile appends in place instead of copying
// every item it already holds into a new mesh.
function capacityFor(count, vertices, indices, headroom) {
  const h = headroom > 0 ? headroom : 0;
  return {
    items: count + Math.ceil(count * h),
    vertices: vertices + Math.ceil(vertices * h),
    indices: Math.max(indices + Math.ceil(indices * h), 1),
  };
}

function groupByKind(items) {
  const groups = new Map();
  for (const item of items) {
    const geometry = normalizeForBatch(item.geometry);
    if (!geometry) continue;
    const blended = !!(item.material && item.material.transparent);
    const kind = blended ? 'blend' : 'opaque';
    let group = groups.get(kind);
    if (!group) {
      group = { kind, blended, material: item.material, entries: [], vertices: 0, indices: 0 };
      groups.set(kind, group);
    }
    const size = sizeOf(geometry);
    group.entries.push({ ...item, geometry });
    group.vertices += size.vertices;
    group.indices += size.indices;
  }
  return groups;
}

// Put one item into a batch mesh and hand back its pool-shaped record,
// or null when the mesh refused it.
function addEntry(mesh, entry, tileKey, records) {
  const ud = mesh.userData;
  let id;
  try {
    id = mesh.addGeometry(entry.geometry);
  } catch (e) {
    // Sized from the geometries, so this should not happen; if it does
    // the item simply does not render here and the tile keeps the rest.
    console.warn('[tileBatch] addGeometry refused an item in', tileKey, e && e.message);
    return null;
  }
  ud.usedVertices += sizeOf(entry.geometry).vertices;
  ud.usedIndices += sizeOf(entry.geometry).indices;
  mesh._batchSphereDirty = true;
  mesh._cullDirty = true;

  // Same decode as GlobalInstancePool.acquire: the C# rows are
  // row-major and need the transpose, binary sidecar rows are
  // already column-major.
  const snapshotMatrices = ud.snapshotMatrices;
  _itemMatrix.fromArray(entry.instance.deltaMatrix);
  if (!entry.instance.cm) _itemMatrix.transpose();
  _itemMatrix.toArray(snapshotMatrices, id * 16);

  // Nothing is shown by the write below: the controller's
  // post-register sweep decides what is visible, which is what
  // keeps a tile from popping in half-hidden.
  mesh.setMatrixAt(id, ZERO_MATRIX);

  const rec = entry.instance;
  rec.id = rec.uniqueId;
  rec.index = id;
  rec.hash = entry.hash;
  rec.tileKey = entry.ownerKey || tileKey;
  if (!Object.prototype.hasOwnProperty.call(rec, 'transformation')) {
    Object.defineProperty(rec, 'transformation', {
      enumerable: false, configurable: true,
      get() {
        const s = snapshotMatrices; const o = rec.index * 16;
        return {
          m0: s[o], m1: s[o + 4], m2: s[o + 8], m3: s[o + 12],
          m4: s[o + 1], m5: s[o + 5], m6: s[o + 9], m7: s[o + 13],
          m8: s[o + 2], m9: s[o + 6], m10: s[o + 10], m11: s[o + 14],
          m12: s[o + 3], m13: s[o + 7], m14: s[o + 11], m15: s[o + 15],
        };
      },
    });
  }
  // The shape this item was built from, kept so the viewer can lift
  // one element out of the shared buffer for a hover silhouette.
  // BatchedMesh does not hand its per-item geometry back, and an
  // outline around the whole batch is the bug this replaced.
  Object.defineProperty(rec, '__pullGeometry', {
    enumerable: false, configurable: true, value: entry.geometry,
  });
  ud[id] = rec;
  mesh.batchItemCount = id + 1;

  const record = {
    mesh,
    batch: mesh,
    uniqueId: rec.uniqueId,
    id: rec.uniqueId,
    index: id,
    fragments: [{ mesh, index: id, batch: mesh }],
  };
  records.set(rec.uniqueId, record);
  return record;
}

function makeBatchMesh(tileKey, group, role, capacity) {
  // Sized from the geometries themselves, never from an estimate: a
  // BatchedMesh allocates its buffers up front and cannot grow, so a
  // guess is either a failure or wasted megabytes per tile.
  const material = group.material ? group.material.clone() : null;
  if (!material) return null;
  material.vertexColors = true;
  attachBatchTint(material, capacity.items);
  applySurfaceShading(material);
  singlePassBlend(material);

  const mesh = new BatchedMesh(capacity.items, capacity.vertices, capacity.indices, material);
  mesh.name = `batch:${tileKey}:${group.kind}`;
  mesh.userData.batchKind = group.kind;
  mesh.userData.batchRole = role;
  // Per-item culling is why a batch can draw fewer triangles than the
  // same items in pools, which never cull per instance. Switchable so
  // the two paths can be compared on equal terms.
  mesh.perObjectFrustumCulled = !(typeof window !== 'undefined' && window.BIDLIGHT_BATCH_NO_CULL === true);
  // No sort for either kind. Opaque: the depth test decides what shows.
  // Blended: these were never sorted (the shells do not write depth).
  mesh.sortObjects = false;
  mesh.frustumCulled = false;
  mesh._cullStats = { kept: 0, whole: 0, culled: 0 };
  mesh._cullDirty = true;
  mesh._batchSphereDirty = true;
  mesh.onBeforeRender = cachedOnBeforeRender;

  const snapshotMatrices = new Float32Array(capacity.items * 16);
  const baseColors = new Float32Array(capacity.items * 3);
  baseColors.fill(1);
  const userData = mesh.userData;
  userData.snapshotMatrices = snapshotMatrices;
  userData.centeredMatrices = snapshotMatrices;
  userData.baseColors = baseColors;
  userData.tileKey = tileKey;
  userData.isTileBatch = true;
  userData.usedVertices = 0;
  userData.usedIndices = 0;

  // The two flag holders the adapter and applicator write to. Neither
  // is read by the renderer for a BatchedMesh - matrices go through
  // its own texture - so they exist purely to keep those call sites
  // branch-free.
  mesh.isSlotMesh = true;
  mesh.instanceMatrix = { needsUpdate: false };
  mesh.instanceColor = { needsUpdate: false };
  mesh.setColorAt = (id, color) => { setBatchTintAt(mesh, id, color); };
  // A PARKED ITEM IS NOT DRAWN AT ALL. Hidden, isolated-out and
  // duplicate items used to stay in the multi-draw list at 1/10,000
  // scale near the origin: no pixels, but their triangles still went to
  // the GPU and into renderer.info whenever the origin was in view.
  // BatchedMesh can drop an item from the list outright, and its raycast
  // skips it too, so the parked matrix now also switches the item off.
  const writeMatrix = mesh.setMatrixAt.bind(mesh);
  mesh.setMatrixAt = (id, matrix) => {
    if (!sameMatrixAt(mesh, id, matrix)) {
      writeMatrix(id, matrix);
      mesh._cullDirty = true;
      mesh._batchSphereDirty = true;
    }
    const show = !isParkedMatrix(matrix);
    if (mesh.getVisibleAt(id) !== show) mesh.setVisibleAt(id, show);
    return mesh;
  };
  mesh.setGhostAt = (id, ghosted) => { setBatchGhostAt(mesh, id, ghosted); };
  mesh.batchItemCount = 0;
  return mesh;
}

// Room left in a batch mesh for these entries?
function fits(mesh, group) {
  const ud = mesh.userData;
  return mesh._geometryCount + group.entries.length <= mesh._maxGeometryCount
    && ud.usedVertices + group.vertices <= mesh._maxVertexCount
    && ud.usedIndices + group.indices <= mesh._maxIndexCount;
}

// items: [{ hash, instance, geometry, material, ownerKey? }]
// Returns null when there is nothing batchable, so the caller can keep
// its pool path untouched.
//
// `ownerKey` is the adapter registration an item belongs to when that is
// not the tile itself - a revive registers its rows under a virtual key
// (`tile#vN`), and the applicator decides whether a row is the drawn copy
// of its element by comparing the row's tileKey with the owner's. Items
// keep it, and the returned batch keeps its items, so a batch can be
// rebuilt with more items appended (see StreamingController._growTileBatch)
// and every existing item lands on the same id with the same owner.
//
// `headroom` (a fraction) sizes the meshes beyond what `items` needs, so
// `append` can add a later revive's rows in place.
export default function buildTileBatch({ tileKey, items, sceneRoot, viewer, role = 'tile', headroom = 0 }) {
  if (!items || items.length === 0) return null;

  // Opaque and blended cannot share a draw: the blended pass needs its
  // own sort order and depth behaviour. The exporter marks a whole
  // fragment blended as soon as one of its triangles is, so the split
  // is per fragment, never per triangle.
  const groups = groupByKind(items);
  if (groups.size === 0) return null;

  const meshes = [];
  const records = new Map();

  groups.forEach((group) => {
    if (group.entries.length === 0) return;
    const mesh = makeBatchMesh(tileKey, group, role,
      capacityFor(group.entries.length, group.vertices, group.indices, headroom));
    if (!mesh) return;
    for (const entry of group.entries) {
      if (!addEntry(mesh, entry, tileKey, records)) break;
    }
    if (sceneRoot) sceneRoot.add(mesh);
    meshes.push(mesh);
  });

  if (meshes.length === 0) return null;

  const batch = {
    tileKey,
    meshes,
    records,
    // What it was built from, in order: a rebuild that starts with these
    // gives every existing item the same id (BatchedMesh ids are
    // sequential per mesh, and the split by kind keeps the order).
    items,
    itemCount: records.size,
    appends: 0,
    // Add rows to this batch IN PLACE: the existing items are not copied,
    // their ids, records and registrations do not move, and only the new
    // rows' ranges are uploaded. Returns Map(uid -> record) of the added
    // rows, or null - and then nothing was added - when a kind has no mesh
    // here or not enough room (the caller rebuilds, with headroom).
    append(added) {
      if (!added || added.length === 0) return new Map();
      const addGroups = groupByKind(added);
      if (addGroups.size === 0) return null;
      const byKind = new Map(meshes.map((m) => [m.userData.batchKind, m]));
      let ok = true;
      addGroups.forEach((group) => {
        const mesh = byKind.get(group.kind);
        if (!mesh || !fits(mesh, group)) ok = false;
      });
      if (!ok) return null;
      const out = new Map();
      addGroups.forEach((group) => {
        const mesh = byKind.get(group.kind);
        for (const entry of group.entries) addEntry(mesh, entry, tileKey, out);
      });
      out.forEach((rec, uid) => records.set(uid, rec));
      for (const it of added) batch.items.push(it);
      batch.itemCount = records.size;
      batch.appends++;
      return out;
    },
    dispose() {
      for (const mesh of meshes) {
        if (mesh.parent) mesh.parent.remove(mesh);
        if (viewer && Array.isArray(viewer.meshes)) {
          const at = viewer.meshes.indexOf(mesh);
          if (at >= 0) viewer.meshes.splice(at, 1);
        }
        disposeBatchTint(mesh.material);
        if (mesh.material) mesh.material.dispose();
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh._matricesTexture) mesh._matricesTexture.dispose();
        mesh.userData = {};
      }
      meshes.length = 0;
      records.clear();
    },
  };
  return batch;
}
